import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, AtSign, Loader2, Check, X, Phone, Music } from 'lucide-react';
import { API } from '../../config/api';
import { checkHandleAvailability as checkHandle } from '../../utils/safeJson';



export default function ProducerSignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingHandle, setCheckingHandle] = useState(false);
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [handleError, setHandleError] = useState('');
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    handle: '',
    phone: '',
    agreedToGuidelines: false
  });
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const checkHandleAvailability = async (handle) => {
    // Cancel previous request
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!handle || handle.length < 3) {
      setHandleAvailable(null);
      return;
    }
    
    if (!/^[a-z0-9_]+$/.test(handle)) {
      setHandleError('Only lowercase letters, numbers, and underscores');
      setHandleAvailable(false);
      return;
    }
    
    debounceTimerRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();
      setCheckingHandle(true);
      setHandleError('');
      
      try {
        const result = await checkHandle(handle, API, abortControllerRef.current.signal);
        if (result.error !== 'aborted') {
          setHandleAvailable(result.available);
          if (!result.available) setHandleError(result.reason || 'Handle not available');
        }
      } catch (err) {
        setHandleError('Could not check availability');
      } finally {
        setCheckingHandle(false);
      }
    }, 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.agreedToGuidelines) {
      toast.error('You must agree to the Community Guidelines');
      return;
    }
    
    if (!handleAvailable) {
      toast.error('Please choose an available @handle');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          name: form.name,
          role: 'producer',
          handle: form.handle,
          phone_number: form.phone,
          agreed_to_guidelines: form.agreedToGuidelines
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail?.message || error.detail || 'Registration failed');
      }
      
      const data = await res.json();
      localStorage.setItem('token', data.access_token);
      
      toast.success('Welcome to DMV Soundstage!');
      navigate('/dashboard/producer');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="producer-signup-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
            <Music className="w-8 h-8 text-orange-400" />
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase mb-2">Join as Producer</h1>
          <p className="text-muted-foreground">Sell beats, collaborate with DMV artists, and get booked for sessions</p>
        </div>

        <Card className="glass-card border-orange-500/20 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Producer Name / Brand</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your producer name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="producer-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="handle">@Handle (required)</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="handle"
                  type="text"
                  placeholder="yourproducerhandle"
                  value={form.handle}
                  onChange={(e) => {
                    const handle = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                    setForm({ ...form, handle });
                    checkHandleAvailability(handle);
                  }}
                  className={`pl-10 pr-10 ${handleAvailable === true ? 'border-green-500' : handleAvailable === false ? 'border-red-500' : ''}`}
                  required
                  data-testid="producer-handle-input"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingHandle && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {!checkingHandle && handleAvailable === true && <Check className="w-4 h-4 text-green-500" />}
                  {!checkingHandle && handleAvailable === false && <X className="w-4 h-4 text-red-500" />}
                </div>
              </div>
              {handleError && <p className="text-xs text-red-400">{handleError}</p>}
              {handleAvailable && <p className="text-xs text-green-400">@{form.handle} is available!</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="producer-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number <span className="text-muted-foreground text-xs">(Optional)</span></Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, '').slice(0, 10) })}
                  className="pl-10"
                  data-testid="producer-phone-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">For account recovery and notifications</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="producer-password-input"
                />
              </div>
            </div>

            {/* Simple Terms Checkbox */}
            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={form.agreedToGuidelines}
                onCheckedChange={(checked) => setForm({ ...form, agreedToGuidelines: checked })}
                data-testid="producer-terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-orange-400 hover:underline">Terms of Service</Link>,{' '}
                <Link to="/privacy" target="_blank" className="text-orange-400 hover:underline">Privacy Policy</Link>, and{' '}
                <Link to="/community-guidelines" target="_blank" className="text-orange-400 hover:underline">Community Guidelines</Link>
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={loading || !handleAvailable || !form.agreedToGuidelines}
              data-testid="producer-signup-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Create Producer Account
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-orange-400 hover:underline">Sign in</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
